import { HelpOutline } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import { Box, InputBase, Tooltip } from '@mui/material';
import { styled, SxProps, useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { BodyText } from '../BodyText';
import { RoundedSquareIconButton } from '../RoundedSquareIconButton';

const SearchBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  '& input[type="search"]::-webkit-search-cancel-button': {
    display: 'none',
  },
}));

const StyledSearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  padding: '8px 8px 2px 12px',
  '& input::placeholder': {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
}));

const StyledSearchIcon = styled(SearchIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const SearchTooltip = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        paddingTop: '6px',
        flexDirection: 'column',
        gap: '2px',
        padding: '0 0 4px 0',
      }}
    >
      <BodyText>Keywords match filename and metadata.</BodyText>
      <BodyText>&quot;Keywords in quotes&quot; require an exact match.</BodyText>
    </Box>
  );
};

export const SearchInput = ({
  initialValue,
  onSearch,
  placeholder = 'Search...',
  AddToSearchButton,
  SearchAttachment,
  sx,
}: {
  initialValue: string;
  onSearch: (query: string) => void;
  AddToSearchButton?: React.ReactNode;
  SearchAttachment?: React.ReactNode;
  placeholder?: string;
  sx?: SxProps;
}) => {
  const theme = useTheme();
  const [value, setValue] = useState(initialValue);
  const [searchEntered, setSearchEntered] = useState(false);
  const inputReference = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleSearchClick = () => {
    onSearch(value);
    setSearchEntered(true);
  };

  return (
    <SearchBox sx={sx}>
      <StyledSearchInput
        ref={inputReference}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        fullWidth
        autoFocus
        type='search'
        onBlur={handleSearchClick}
        endAdornment={
          <Tooltip
            title={<SearchTooltip />}
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: '600px !important',
                },
              },
            }}
          >
            <HelpOutline
              sx={{ fontSize: '17px', margin: '0 5px', cursor: 'default', color: theme.palette.text.secondary }}
            />
          </Tooltip>
        }
      />
      <Box display='flex' width='100%' flexDirection='row' alignItems='center' justifyContent='flex-start'>
        {SearchAttachment}
      </Box>
      <Box display='flex' width='100%' flexDirection='row' alignItems='center' justifyContent='space-between'>
        {AddToSearchButton}
        <Tooltip title='Submit'>
          <RoundedSquareIconButton
            size='small'
            onClick={handleSearchClick}
            sx={{
              margin: '6px',
              background: value?.trim() && !searchEntered ? 'rgba(56, 119, 85, 0.15)' : 'transparent',
            }}
          >
            {value?.trim() ? <SendIcon sx={{ fontSize: '18px' }} /> : <StyledSearchIcon sx={{ fontSize: '18px' }} />}
          </RoundedSquareIconButton>
        </Tooltip>
      </Box>
    </SearchBox>
  );
};
